import { Box, Button, Skeleton, Stack, Typography } from '@mui/material'
import { Trans } from 'react-i18next'
import { Divider, IconButton, Icons, SkeletonText } from 'utoniq-ui'

import { Maybe, TicketShareConfirmFragment } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { TicketLabel } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  receiving: boolean
  data: Maybe<TicketShareConfirmFragment>
  ticketNumber: string
  formattedExpiredAt: Maybe<string>
  invalidTicketDistribution: boolean
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onReceive: () => void
}

export const TicketShareConfirmTemplate: Template<State, Action> = ({
  state,
  onReceive,
}) => {
  const { t } = useTranslation('ticketShareConfirm')
  const { router, generatePath, routerPages, routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.data?.item?.issuedArtist?.id}>
      <BaseLayout
        header={{
          back: false,
          title: t('title'),
          right: (
            <IconButton.Close
              edge="start"
              onClick={() =>
                router.replace(generatePath(routerPages.GlobalHome))
              }
            />
          ),
        }}
      >
        <Head title={routerNames.TicketShareConfirm} />
        {state.loading ? (
          <TicketShareConfirmSkeleton />
        ) : (
          <TicketShareConfirmCell
            loading={state.receiving}
            data={state.data}
            ticketNumber={state.ticketNumber}
            formattedExpiredAt={state.formattedExpiredAt}
            invalidTicketDistribution={state.invalidTicketDistribution}
            onReceive={onReceive}
          />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type TicketShareConfirmCellProps = {
  loading: boolean
  invalidTicketDistribution: boolean
  data: Maybe<TicketShareConfirmFragment>
  ticketNumber: string
  formattedExpiredAt: Maybe<string>
  onReceive: () => void
}
const TicketShareConfirmCell: Cell<TicketShareConfirmCellProps> = ({
  loading,
  invalidTicketDistribution,
  data,
  ticketNumber,
  onReceive,
}) => {
  const { t } = useTranslation('ticketShareConfirm')
  const { handleLoginModal } = usePrivateAction()

  if (!data || data?.userId) {
    return (
      <CellFrame sx={{ px: 5 }}>
        <Typography variant="h4" py={5}>
          {t('checkTicketDetails')}
        </Typography>
        <TicketShareConfirmNoticeCell data={data} />
      </CellFrame>
    )
  }

  return (
    <CellFrame sx={{ px: 5 }}>
      <Typography variant="h4" py={5}>
        {t('checkTicketDetails')}
      </Typography>
      <TicketShareConfirmNoticeCell data={data} />
      {data?.item?.preSale?.title && (
        <>
          <Stack spacing={1} sx={{ py: 2 }}>
            <Typography variant="subtitle1" color="text.secondary">
              {t('preSale')}
            </Typography>
            <Typography variant="body2">
              {data?.item?.preSale?.title}
            </Typography>
          </Stack>
          <Divider />
        </>
      )}
      <Stack spacing={1} sx={{ py: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('ticketInformation')}
        </Typography>
        <Stack>
          <Typography variant="body2">{data?.item?.title}</Typography>
          {!!data?.serial?.sku?.unitName && (
            <Typography variant="body2">
              {data?.serial?.sku?.unitName}
            </Typography>
          )}
        </Stack>
      </Stack>
      <Divider />
      <Stack spacing={1} sx={{ py: 2 }}>
        <TicketLabel
          serial={data?.serial}
          assignedNumberType={data?.item?.metadata?.ticket?.assignedNumberType}
          typographyProps={{ variant: 'subtitle1', color: 'text.secondary' }}
        />
        {data?.serial?.ticketStatus?.throughTicket?.map(
          (throughTicket, index) => (
            <Stack
              key={index}
              spacing={1}
              pb={
                (data?.serial?.ticketStatus?.throughTicket?.length ?? 0) >
                index + 1
                  ? 1
                  : 0
              }
            >
              <Typography variant="subtitle2" color="text.secondary">
                {throughTicket.name}
              </Typography>
              <Typography variant="body2">
                {throughTicket.seat ?? ticketNumber}
              </Typography>
            </Stack>
          )
        )}
        {!data?.serial?.ticketStatus?.throughTicket?.length && (
          <Typography variant="body2">{ticketNumber}</Typography>
        )}
      </Stack>
      {!invalidTicketDistribution && (
        <Stack sx={{ p: 4 }} spacing={3} alignItems="center">
          <Button
            size="large"
            loading={loading}
            sx={{ width: 244 }}
            onClick={handleLoginModal(onReceive)}
          >
            {t('receiveTicket')}
          </Button>
        </Stack>
      )}
    </CellFrame>
  )
}

type TicketShareConfirmNoticeCellProps = {
  data?: Maybe<TicketShareConfirmFragment>
}
const TicketShareConfirmNoticeCell: Cell<TicketShareConfirmNoticeCellProps> = ({
  data,
}) => {
  const { t } = useTranslation('ticketShareConfirm')
  const { onClick } = useBrowserLink(getHelpUrl('ticketDistribution'))

  if (!data) {
    return (
      <Stack
        spacing={1}
        sx={{
          p: 3,
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
          <Typography variant="subtitle2">
            {t('urlInvalid', '受け取りができません')}
          </Typography>
        </Stack>
        <Typography variant="caption">
          {t(
            'urlInvalidNote3',
            'このチケットは分配URLが無効のため受け取りできません。'
          )}
        </Typography>
      </Stack>
    )
  }

  if (data?.userId) {
    return (
      <Stack
        spacing={1}
        sx={{
          p: 3,
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
          <Typography variant="subtitle2">
            {t('urlInvalid', '受け取りができません')}
          </Typography>
        </Stack>
        <Typography variant="caption">
          {t('urlInvalidNote4', 'このチケットはすでに受け取られています。')}
        </Typography>
      </Stack>
    )
  }

  if (data?.isExpired) {
    return (
      <Stack
        spacing={1}
        sx={{
          p: 3,
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
          <Typography variant="subtitle2">
            {t('urlInvalid', '受け取りができません')}
          </Typography>
        </Stack>
        <Typography variant="caption">
          <Trans
            i18nKey="urlInvalidNote2"
            t={t}
            components={{
              linkTag: (
                <Typography
                  component="span"
                  variant="caption"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                  onClick={onClick}
                />
              ),
            }}
          />
        </Typography>
      </Stack>
    )
  }

  if (data?.item.ownedCount) {
    return (
      <Stack
        spacing={1}
        sx={{
          p: 3,
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
          <Typography variant="subtitle2">
            {t('urlInvalid', '受け取りができません')}
          </Typography>
        </Stack>
        <Typography variant="caption">{t('urlInvalidNote1')}</Typography>
      </Stack>
    )
  }

  if (data?.formattedExpiredAt) {
    return (
      <Typography
        variant="caption"
        align="center"
        color="error.main"
        component="p"
        p={2}
        sx={{
          width: '100%',
          borderColor: 'error.main',
          borderWidth: 1,
          borderStyle: 'solid',
        }}
      >
        {t('receiptExpirationDate', { expiredAt: data.formattedExpiredAt })}
      </Typography>
    )
  }

  return null
}

const TicketShareConfirmSkeleton: Cell = () => (
  <CellFrame sx={{ px: 5 }}>
    <Box sx={{ py: 6 }}>
      <Skeleton variant="rectangular" height="20px" width="50%" />
    </Box>
    <Stack spacing={3} sx={{ py: 3 }}>
      <Skeleton variant="rectangular" height="15px" width="25%" />
      <Stack spacing={2}>
        <SkeletonText variant="body2" width="100%" />
        <SkeletonText variant="body2" width="45%" />
      </Stack>
    </Stack>
    <Divider />
    <Stack spacing={3} sx={{ py: 3 }}>
      <Skeleton variant="rectangular" height="15px" width="25%" />
      <Stack spacing={2}>
        <SkeletonText variant="body2" width="100%" />
        <SkeletonText variant="body2" width="45%" />
      </Stack>
    </Stack>
    <Divider />
    <Stack spacing={3} sx={{ py: 3 }}>
      <Skeleton variant="rectangular" height="15px" width="25%" />
      <SkeletonText variant="body2" width="45%" />
    </Stack>
    <Stack sx={{ py: 6, width: '100%' }} alignItems="center">
      <Skeleton
        variant="rectangular"
        height="44px"
        width="50%"
        sx={{ borderRadius: '34px' }}
      />
    </Stack>
  </CellFrame>
)
